import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import { useMovementsByAsset } from '../hooks';
import { MovementTypeBadge, MOVEMENT_TYPE_LABELS } from './MovementTypeBadge';
import type { MovementType } from '../api';
import { formatarData, formatarOrigemDestino } from '../utils/formatters';
import { TextInput } from '../../../shared/components/TextInput';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';

interface Props {
  open: boolean;
  onClose: () => void;
  assetId: string;
}

const TYPE_OPTIONS: Array<[MovementType, string]> = Object.entries(MOVEMENT_TYPE_LABELS) as Array<[MovementType, string]>;

export function MovimentacoesModal({ open, onClose, assetId }: Props) {
  const [type, setType] = useState<MovementType | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const LIMIT = 20;
  const query = useMovementsByAsset(assetId, {
    type: type || undefined,
    from: from || undefined,
    to: to || undefined,
    limit: LIMIT,
    offset: page * LIMIT,
  });

  const items = query.data?.data ?? [];
  const total = query.data?.pagination.count ?? 0;
  const maxPage = Math.max(0, Math.ceil(total / LIMIT) - 1);

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-4xl rounded-lg bg-white p-6 shadow-xl">
                <div className="mb-4 flex items-center justify-between">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">Histórico completo de movimentações</Dialog.Title>
                  <button onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100">
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                  <AutocompleteSelect
                    label="Tipo"
                    options={[
                      { value: '', label: 'Todos os tipos' },
                      ...TYPE_OPTIONS.map(([v, l]) => ({ value: v, label: l })),
                    ]}
                    value={type}
                    onChange={(v) => { setType(v as MovementType | ''); setPage(0); }}
                  />
                  <TextInput
                    label="Data inicial"
                    type="date"
                    value={from}
                    onChange={(e) => { setFrom(e.target.value); setPage(0); }}
                  />
                  <TextInput
                    label="Data final"
                    type="date"
                    value={to}
                    onChange={(e) => { setTo(e.target.value); setPage(0); }}
                  />
                </div>

                <div className="max-h-[60vh] overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Data</th>
                        <th className="px-3 py-2 text-left">Tipo</th>
                        <th className="px-3 py-2 text-left">Descrição</th>
                        <th className="px-3 py-2 text-left">Responsável (origem → destino)</th>
                        <th className="px-3 py-2 text-left">Localização (origem → destino)</th>
                        <th className="px-3 py-2 text-left">Executor</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {items.length === 0 && (
                        <tr><td colSpan={6} className="py-6 text-center text-sm text-gray-500">Nenhum movimento encontrado.</td></tr>
                      )}
                      {items.map((m) => {
                        const dt = new Date(m.occurredAt);
                        return (
                          <tr key={m.id}>
                            <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                              {formatarData(m.occurredAt)}
                              <span className="ml-1 text-xs text-gray-500">
                                {dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </td>
                            <td className="px-3 py-2"><MovementTypeBadge type={m.type} /></td>
                            <td className="px-3 py-2 text-gray-700">{m.description}</td>
                            <td className="px-3 py-2 text-gray-700">
                              {formatarOrigemDestino(m.previousCustodianName, m.newCustodianName)}
                            </td>
                            <td className="px-3 py-2 text-gray-700">
                              {formatarOrigemDestino(m.previousLocationName, m.newLocationName ?? m.locationName)}
                            </td>
                            <td className="px-3 py-2 text-gray-700">
                              {m.performedByUserName ?? m.performedByUserEmail ?? '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 flex items-center justify-between text-sm">
                  <span className="text-gray-500">{total} movimento{total === 1 ? '' : 's'}</span>
                  <div className="flex items-center gap-2">
                    <button
                      disabled={page === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      className="rounded border border-gray-300 px-2 py-1 disabled:opacity-40"
                    >
                      Anterior
                    </button>
                    <span>Página {page + 1} de {maxPage + 1}</span>
                    <button
                      disabled={page >= maxPage}
                      onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
                      className="rounded border border-gray-300 px-2 py-1 disabled:opacity-40"
                    >
                      Próxima
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
